import { useEffect, useMemo } from 'react';

import { appActions } from '../store/appActions';
import { useUiStore } from '../store/useUiStore';
import { LanguageContext } from '../../shared/contexts/languageContext';
import { 
  translations, 
  availableLanguages, 
  getLanguageByCode, 
  defaultLanguage, 
  getBrowserLanguage 
} from '../../shared/utils/languages';
import useAuth from '../../shared/hooks/useAuth';
import type { CommonType } from '../../shared/types/CommonType';
import type { LanguageContextType, LanguageCode } from '../../shared/types/LanguageType';

export const LanguageProvider = ({ children }: CommonType) => {
  const { authenticated } = useAuth();
  const currentLang = useUiStore((state) => state.currentLang);
  const isInitialized = useUiStore((state) => state.isInitialized);
  const setLang = useUiStore((state) => state.setLang);
  const initializeLang = appActions.initializeLang;
  
  const language = useMemo(() => {
    return getLanguageByCode(currentLang as LanguageCode);
  }, [currentLang]);

  const getNestedValue = (obj: any, path: string): string => {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj) || path;
  };

  const t = (key: string, params?: Record<string, string | number>): string => {
    let translation = getNestedValue(translations[language.code], key) || 
      getNestedValue(translations[defaultLanguage], key) || key;

    if (params) {
      Object.entries(params).forEach(([paramKey, paramValue]) => {
        translation = translation.replace(`{{${paramKey}}}`, String(paramValue));
      });
    }

    return translation;
  }

  const setLanguage = (langCode: LanguageCode) => {
    setLang(langCode);
    document.documentElement.lang = langCode;
  };

  useEffect(() => {
    if (authenticated) {
      document.documentElement.lang = currentLang;
    } else if (!isInitialized) {
      const browserLang = getBrowserLanguage();
      initializeLang(browserLang);
      document.documentElement.lang = browserLang;
    } else {
      document.documentElement.lang = currentLang;
    }
  }, [currentLang, isInitialized, authenticated, initializeLang]);

  const value: LanguageContextType = {
    language,
    setLanguage,
    t,
    availableLanguages,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};